// src/pages/admin/AdminHomePage.tsx
import React, { useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAdminFlags } from "hooks/useAdminFlags";

type LineGeom = {
  left: number;
  titleTop: number;
  titleBottom: number;
  containerTop: number;
  containerBottom: number;
};

const AdminHomePage: React.FC = () => {
  const { isSuperadmin } = useAdminFlags();

  const pageRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [geom, setGeom] = useState<LineGeom | null>(null);

  useLayoutEffect(() => {
    const page = pageRef.current;
    const title = titleRef.current;
    const container = containerRef.current;
    if (!page || !title || !container) return;

    const compute = () => {
      const p = page.getBoundingClientRect();
      const t = title.getBoundingClientRect();
      const c = container.getBoundingClientRect();

      const left = Math.round(t.left + t.width / 2 - p.left);

      setGeom({
        left: Math.max(0, left),
        titleTop: Math.max(0, Math.round(t.top - p.top)),
        titleBottom: Math.max(0, Math.round(t.bottom - p.top)),
        containerTop: Math.max(0, Math.round(c.top - p.top)),
        containerBottom: Math.max(0, Math.round(c.bottom - p.top))
      });
    };

    compute();

    const ro = new ResizeObserver(() => compute());
    ro.observe(page);
    ro.observe(title);
    ro.observe(container);

    window.addEventListener("resize", compute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", compute);
    };
  }, [isSuperadmin]);

  const TITLE_GAP_PX = 14;
  const CONTAINER_TOP_MARGIN_CLASS = "mt-16 sm:mt-20";

  const cards: Array<{
    to: string;
    title: string;
    desc: string;
  }> = [
    {
      to: "/admin/orders",
      title: "Manage orders",
      desc: "View, update, and track customer orders, including booking and fulfillment status."
    },
    {
      to: "/admin/abandoned-checkouts",
      title: "Abandoned checkouts",
      desc: "Customers who opened checkout and did not pay, to follow up by email."
    },
    {
      to: "/admin/funnel-campaigns",
      title: "Funnel campaign names",
      desc: "The campaign names the app funnel may send to analytics. Add or remove one."
    },
    {
      to: "/admin/packages",
      title: "Manage packages",
      desc: "Create, update, publish, and archive packages."
    },
    {
      to: "/admin/clinics",
      title: "Manage clinics",
      desc: "Add and edit partner clinics, update details, publish listings."
    },
    {
      to: "/admin/notifications",
      title: "Delivery notifications",
      desc: "View, retry, and acknowledge outbound notifications (e.g., clinic emails)."
    }
  ];

  if (isSuperadmin) {
    cards.push({
      to: "/superadmin/admins",
      title: "Manage admins",
      desc: "Search users, grant or revoke admin role."
    });
  }

  return (
    <main ref={pageRef} className="relative min-h-screen px-4 py-12 sm:py-16">
      {/* Vertical gold line segments (with gaps for title + container) */}
      {geom && (
        <>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: 0,
              bottom: `calc(100% - ${Math.max(0, geom.titleTop - TITLE_GAP_PX)}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.titleBottom + TITLE_GAP_PX,
              bottom: `calc(100% - ${geom.containerTop}px)`
            }}
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-0 w-px bg-[#f3dfae]/90"
            style={{
              left: geom.left,
              top: geom.containerBottom,
              bottom: 0
            }}
          />
        </>
      )}

      <div className="relative z-10">
        {/* Title */}
        <header className="text-center">
          <h1
            ref={titleRef}
            className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#ceddf2]"
          >
            Admin portal
          </h1>
        </header>

        {/* Main container */}
        <section className={[CONTAINER_TOP_MARGIN_CLASS, "flex justify-center"].join(" ")}>
          <div
            ref={containerRef}
            className="w-full md:w-3/5 bg-white rounded-2xl shadow-sm border border-slate-200 px-6 py-8 sm:px-8"
          >
            <div className="flex items-start justify-between gap-4">
              <p className="font-ui text-xs uppercase tracking-wide text-slate-500">
                Choose a section to manage
              </p>
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {cards.map((c) => (
                <Link
                  key={c.to}
                  to={c.to}
                  className={[
                    "block rounded-2xl p-6",
                    "border border-[#f3dfae]/70",
                    "bg-white",
                    "shadow-sm hover:shadow transition-shadow"
                  ].join(" ")}
                >
                  <h2 className="font-display text-xl font-semibold text-slate-900">
                    {c.title}
                  </h2>
                  <p className="mt-2 font-body text-sm text-slate-700 leading-relaxed">
                    {c.desc}
                  </p>

                  <div className="mt-4">
                    <span
                      className={[
                        "inline-flex items-center justify-center rounded-full",
                        "bg-[#f3dfae] px-4 py-2",
                        "text-xs tracking-[0.18em] uppercase text-[#151A3A]",
                        "font-ui font-bold"
                      ].join(" ")}
                    >
                      Open
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {isSuperadmin && (
              <p className="mt-6 font-ui text-xs uppercase tracking-wide text-slate-500">
                Superadmin tools enabled
              </p>
            )}
          </div>
        </section>
      </div>
    </main>
  );
};

export default AdminHomePage;
